import {
  Avatar,
  AvatarGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getIcon } from 'src/utils/hex-to-rgba';
import { statusObj } from 'src/utils/shopInfo';
import { apiService } from '../../api/axiosClient';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';

const currencyLocale = Intl.NumberFormat('ko-KR');

const columns = [
  {
    field: '__check__',
    hideable: false,
    sortable: false,
    filterable: false,
    headerAlign: 'left'
  },
  {
    flex: 0.2,
    minWidth: 200,
    field: 'orderid',
    hideable: false,
    headerName: '주문번호',
    renderCell: params => (
      <Link to={`/admin/order/${params.row.orderid}`} >
        <Stack direction="row" spacing={4}>
          <Typography variant='body2' sx={{ color: 'text.primary', marginRight: 2 }}>
            {params.row.orderid}
          </Typography>
          <AvatarGroup max={4}>
            {
              params.row.cartlist.map((item, index) => {
                return <Avatar key={index} alt={item.sku} src={getIcon(item.icon1)} variant='circular' sx={{ width: 28, height: 28 }} />
              })            
            }
          </AvatarGroup>
        </Stack>
      </Link>
    )
  },
  {
    flex: 0.1,
    field: 'order_final',
    minWidth: 80,
    headerName: '주문가',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {currencyLocale.format(params.row.order_final)}
      </Typography>
    )
  },
  {
    flex: 0.1,
    field: 'order_time',
    minWidth: 140,
    headerName: '주문시간',
    renderCell: params => (
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {params.row.order_time}
      </Typography>
    )
  },
  {
    flex: 0.1,
    minWidth: 80,
    field: 'order_status',
    headerName: '진행상태',
    renderCell: params => {
      const status = statusObj[params.row.order_status]

      return (
        <CustomChip
          size='small'
          skin='light'
          color={status.color}
          label={status.title}
          sx={{ '& .MuiChip-label': { textTransform: 'capitalize' } }}
        />
      )
    }
  }
]

const TableMyOrder = ({username}) => {
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(0)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('desc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('orderid')
  const [selected, setSelected] = useState([])

  const loadOrders = 
    async (query, sort, column) => {
      try {
        const response = await apiService.get('/orders/history', {
          params: {
            page: page+1,
            pageSize,
            username: username,
            name: query,
            begin: "",
            end: ""
          }
        });
        const payload = response.data;
        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    }

  useEffect(() => {
    loadOrders(searchValue, sort, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadOrders(searchValue, newModel[0].sort, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('orderid')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadOrders(value, sort, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  return (
    loading ?
      <Spinner/> :
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.orderid}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            reload,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.orderid),
          );

          setSelected(selectedRows)
        }}
      />
  )
}

export default TableMyOrder
